import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useAuthTheme, getAuthStyles, injectAuthKeyframes } from './AuthTheme';

injectAuthKeyframes();

const PARTICLES = [
  { top: '10%', left: '8%', animationDelay: '0s' },
  { top: '20%', right: '10%', animationDelay: '1s' },
  { top: '60%', left: '5%', animationDelay: '2s' },
  { bottom: '20%', right: '8%', animationDelay: '0.5s' },
  { bottom: '10%', left: '20%', animationDelay: '1.5s' },
  { top: '45%', right: '5%', animationDelay: '2.5s' },
];

// SVG shield+crown logo — colors adapt to dark/light mode
function AuthorityLogo({ dark }) {
  const shieldColor  = dark ? '#667eea' : '#ffffff';
  const crownColor   = dark ? '#f59e0b' : '#fbbf24';
  const glowColor    = dark ? 'rgba(102,126,234,0.35)' : 'rgba(255,255,255,0.35)';
  const ringBg       = dark ? 'rgba(102,126,234,0.2)' : 'rgba(255,255,255,0.2)';
  const ringBorder   = dark ? 'rgba(102,126,234,0.5)' : 'rgba(255,255,255,0.45)';

  return (
    <div style={{
      width: 88, height: 88, margin: '0 auto 10px',
      background: ringBg,
      borderRadius: '50%',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      border: `2px solid ${ringBorder}`,
      position: 'relative',
      backdropFilter: 'blur(12px)',
      animation: 'logoPulse 3s ease-in-out infinite',
      boxShadow: `0 0 24px ${glowColor}`,
    }}>
      <svg width="46" height="46" viewBox="0 0 46 46" fill="none" xmlns="http://www.w3.org/2000/svg">
        {/* Shield body */}
        <path
          d="M23 3L5 10V22C5 31.5 13 39.5 23 43C33 39.5 41 31.5 41 22V10L23 3Z"
          fill={shieldColor}
          fillOpacity="0.18"
          stroke={shieldColor}
          strokeWidth="2"
          strokeLinejoin="round"
        />
        {/* Crown */}
        <path
          d="M14 28V20L17.5 23L23 17L28.5 23L32 20V28H14Z"
          fill={crownColor}
          stroke={crownColor}
          strokeWidth="0.5"
          strokeLinejoin="round"
        />
        {/* Crown dots */}
        <circle cx="14" cy="19" r="1.8" fill={crownColor} />
        <circle cx="23" cy="16" r="1.8" fill={crownColor} />
        <circle cx="32" cy="19" r="1.8" fill={crownColor} />
        {/* Bottom bar */}
        <rect x="14" y="28" width="18" height="2.5" rx="1.25" fill={crownColor} />
      </svg>
      {/* Glow ring */}
      <div style={{
        position: 'absolute', inset: -4, borderRadius: '50%',
        background: `radial-gradient(circle, ${glowColor} 0%, transparent 65%)`,
        animation: 'glowPulse 2.5s ease-in-out infinite',
      }} />
    </div>
  );
}

export default function AuthorityLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();
  const { dark } = useAuthTheme();
  const s = getAuthStyles();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    if (!email.trim()) { setError('Email is required'); setLoading(false); return; }
    if (!password.trim()) { setError('Password is required'); setLoading(false); return; }
    try {
      const result = await login(email, password, 'authority');
      if (result?.success) {
        navigate('/dashboard');
      } else {
        setError(result?.error || 'Invalid credentials');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={s.page}>
      <div style={s.orb1} /><div style={s.orb2} /><div style={s.orb3} />
      <div style={s.particles}>
        {PARTICLES.map((p, i) => <div key={i} style={{ ...s.particle, ...p }} />)}
      </div>

      {/* Theme toggle — text only, no emoji */}

      <div style={s.wrap}>
        {/* Brand */}
        <div style={s.brand} onClick={() => navigate('/')}>
          <AuthorityLogo dark={dark} />
          <h1 style={s.brandName}>UniResolve</h1>
          <p style={s.brandSub}>Authority Portal</p>
        </div>

        {/* Card */}
        <div style={s.card}>
          <div style={s.cardTop}>
            <div style={s.cardTopBadge}>Authority Login</div>
          </div>
          <div style={s.cardBody}>
            <h2 style={s.heading}>Authority Access</h2>
            <p style={s.sub}>University Authority — Oversight Dashboard</p>
            <form onSubmit={handleSubmit}>
              {error && (
                <div style={s.err}>
                  <span>{error}</span>
                  <button type="button" onClick={() => setError('')} style={s.errX}>x</button>
                </div>
              )}
              <div style={s.field}>
                <label style={s.label}>Authority Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="e.g. xyz@authority.edu"
                  style={s.input}
                  required
                />
              </div>
              <div style={s.field}>
                <label style={s.label}>Password</label>
                <div style={s.pwWrap}>
                  <input
                    type={showPw ? 'text' : 'password'}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    style={s.input}
                    required
                  />
                  <button type="button" onClick={() => setShowPw(!showPw)} style={s.pwBtn}>
                    {showPw ? 'Hide' : 'Show'}
                  </button>
                </div>
              </div>
              <button
                type="submit"
                disabled={loading}
                style={{ ...s.btn, ...(loading ? s.btnOff : {}) }}
              >
                {loading
                  ? <span style={s.btnIn}><span style={s.spin} /> Signing in...</span>
                  : 'Access Authority Dashboard'
                }
              </button>
            </form>
          </div>
        </div>

        <p style={s.footer}>2026 UniResolve</p>
      </div>
    </div>
  );
}